import React from 'react'
import { Vertical, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { BarChart, Bar, ResponsiveContainer, Tooltip } from 'recharts'
import { Skeleton } from 'antd'
import { DeltaDisplay } from '../Launchpad/components/ui/DeltaDisplay'
import { formatMetricNumber, formatDollarMetric } from '../Launchpad/components/utils/formatMetricNumber'

interface MetricCardProps {
  title: string
  value: string | number
  trend?: number
  sparkData: Array<{ value: number }>
  isLoading?: boolean
  invertColors?: boolean
  prefix?: string
  suffix?: string
  formatValue?: (value: string | number) => string
  noTrendData?: boolean
  compact?: boolean
}

export function MetricCard({
  title,
  value,
  trend = 0,
  sparkData,
  isLoading = false,
  invertColors = false,
  prefix = '',
  suffix = '',
  formatValue,
  noTrendData = false,
  compact = false,
}: MetricCardProps) {
  const displayValue = formatValue ? formatValue(value) : `${prefix}${value}${suffix}`

  return (
    <Vertical
      height='auto'
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: 6,
        padding: compact ? 6 : 8,
        flex: 1,
        minWidth: 0,
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <Texto category={compact ? 'p2' : 'p1'} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        {title}
      </Texto>
      {isLoading ? (
        <Horizontal gap={5} style={{ width: '100%', minWidth: 0 }} alignItems='center' justifyContent='space-between'>
          <Skeleton.Input active style={{ width: 120, height: 32 }} />
          <Skeleton.Input active style={{ width: 180, height: 70 }} />
        </Horizontal>
      ) : (
        <Horizontal
          gap={5}
          style={{
            width: '100%',
            minWidth: 0,
            flexWrap: 'nowrap',
            overflow: 'visible',
          }}
          alignItems='center'
          justifyContent='space-between'
        >
          <Vertical gap={2} alignItems='flex-start' style={{ flexShrink: 0 }}>
            <Texto
              category={compact ? 'h4' : 'h3'}
              weight='bold'
              style={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {displayValue}
            </Texto>
            {trend !== undefined && (
              <DeltaDisplay
                value={trend}
                invertColors={invertColors}
                noDataMessage={noTrendData ? 'No trend data' : undefined}
              />
            )}
          </Vertical>

          {/* Spark Chart */}
          <Horizontal
            style={{ width: compact ? 120 : 180, height: compact ? 50 : 70, flexShrink: 0, overflow: 'visible' }}
          >
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={sparkData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }} barCategoryGap='5%'>
                <Tooltip
                  formatter={(value: number) => {
                    // Format based on the metric type
                    if (title.toLowerCase().includes('percentage') || suffix === '%') {
                      return `${value.toFixed(1)}%`
                    } else if (
                      title.toLowerCase().includes('balance') ||
                      title.toLowerCase().includes('due') ||
                      prefix === '$'
                    ) {
                      return formatDollarMetric(value)
                    } else {
                      return formatMetricNumber(value)
                    }
                  }}
                  labelFormatter={(index: number) => sparkData[index]?.date || ''}
                  labelStyle={{ color: '#666' }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    zIndex: 9999,
                  }}
                  wrapperStyle={{
                    zIndex: 9999,
                    marginTop: '50px',
                  }}
                  allowEscapeViewBox={{ x: true, y: true }}
                  offset={-110}
                />
                <Bar dataKey='value' fill='#76abf2' radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Horizontal>
        </Horizontal>
      )}
    </Vertical>
  )
}
